import React, { useState } from 'react';
import { Grid, Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import { ExpandMore, Code, Person, SmartToy, Bolt, CallSplit, Storage, WarningAmber } from '@mui/icons-material';
import { isAnyForm } from '../../../../utils/formTypeConstants';

export const isHumanAction = component => {
  const componentType = component?.name;
  return (
    component?.isHumanAction ||
    isAnyForm(componentType) ||
    componentType === 'assign_task'
  );
};

export const isWorkflowOperation = component => {
  return (
    component.name.includes('branch_path') ||
    component.name.includes('merge_workflow') ||
    component.name.includes('loop_through_list') ||
    component.name.includes('converge_threads') ||
    component.name.includes('trigger_another_workflow') ||
    component.name.includes('trigger_from_another_workflow') ||
    component.name === 'gateway'
  );
};

export const isTableOperation = component => {
  return (
    component.name.includes('query_table_v2') ||
    component.name.includes('create_row_v2') ||
    component.name.includes('update_row_v2') ||
    component.name.includes('trigger_table_v2')
  );
};

export const getCategoryColors = category => {
  return { background: 'rgba(0, 0, 0, 0.03)' };
};

export const LegacyWarning = () => (
  <Grid
    container
    alignItems="flex-start"
    sx={{
      padding: '6px 10px',
      marginBottom: '4px',
      backgroundColor: 'rgba(255, 152, 0, 0.08)',
      borderLeft: '3px solid #ff9800',
      borderRadius: '4px',
    }}
  >
    <WarningAmber sx={{ color: '#e65100', fontSize: 16, marginRight: '6px', marginTop: '1px' }} />
    <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
      These components are no longer actively maintained and may be removed in a future update.
    </Typography>
  </Grid>
);

export const ActionTypeGroup = ({ title, icon, groupId, components, isExpanded, onToggle }) => {
  const colors = getCategoryColors(groupId);
  const isLegacy = groupId === 'legacy';

  const handleChange = () => {
    if (onToggle) {
      onToggle(groupId);
    }
  };

  return (
    <Accordion
      expanded={isExpanded}
      onChange={handleChange}
      sx={{
        boxShadow: 'none',
        border: '1px solid',
        borderColor: isLegacy ? 'rgba(0,0,0,0.08)' : 'divider',
        borderRadius: '8px !important',
        marginBottom: '6px',
        overflow: 'hidden',
        opacity: isLegacy ? 0.85 : 1,
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          background: colors.background,
          borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
          padding: '4px 8px',
          minHeight: '40px',
          '&.Mui-expanded': {
            minHeight: '40px',
          },
          '&:hover': {
            background: colors.background,
            opacity: 0.9,
            transition: '0.3s',
          },
        }}
      >
        <Grid container spacing={2} paddingRight={1}>
          <Grid item sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon}
          </Grid>
          <Grid item xs>
            <Grid container>
              <Grid item xs={12}>
                <span style={{ fontSize: 14, fontWeight: 'bold', lineHeight: '18px' }}>
                  {title}
                </span>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: '4px 8px',
          bgcolor: 'background.paper',
          borderRadius: '0 0 8px 8px',
        }}
      >
        {isLegacy && <LegacyWarning />}
        <Grid container spacing={1}>
          {components}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export const AlwaysExpandedActionTypeGroup = ({ title, icon, groupId, components }) => {
  const colors = getCategoryColors(groupId);

  return (
    <Accordion
      expanded={true}
      sx={{
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px !important',
        marginBottom: '6px',
        overflow: 'hidden',
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore style={{ visibility: 'hidden' }} />}
        sx={{
          background: colors.background,
          borderRadius: '8px 8px 0 0',
          padding: '4px 8px',
          minHeight: '40px',
          '&.Mui-expanded': {
            minHeight: '40px',
          },
          pointerEvents: 'none',
          '&:hover': {
            background: colors.background,
          },
        }}
      >
        <Grid container spacing={2} paddingRight={1}>
          <Grid item sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon}
          </Grid>
          <Grid item xs>
            <Grid container>
              <Grid item xs={12}>
                <span style={{ fontSize: 14, fontWeight: 'bold', lineHeight: '18px' }}>
                  {title}
                </span>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: '4px 8px',
          bgcolor: 'background.paper',
          borderRadius: '0 0 8px 8px',
        }}
      >
        <Grid container spacing={1}>
          {components}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export const AIIconSVG = () => (
  <svg width="0" height="0" style={{ position: 'absolute' }}>
    <symbol id="ai-stars-icon" viewBox="0 0 16 16">
      <path d="M13.0909 5.81818L12.1818 3.81818L10.1818 2.90909L12.1818 2L13.0909 0L14 2L16 2.90909L14 3.81818L13.0909 5.81818ZM13.0909 16L12.1818 14L10.1818 13.0909L12.1818 12.1818L13.0909 10.1818L14 12.1818L16 13.0909L14 14L13.0909 16ZM5.81818 13.8182L4 9.81818L0 8L4 6.18182L5.81818 2.18182L7.63636 6.18182L11.6364 8L7.63636 9.81818L5.81818 13.8182Z" />
    </symbol>
  </svg>
);

export const getCategoryIcon = category => {
  const iconStyles = {
    triggers: { color: '#f9a825' }, // Amber color for triggers
    aiActions: { color: '#7b1fa2' }, // Purple for AI
    humanActions: { color: '#0277bd' }, // Blue for human actions
    codeActions: { color: '#388e3c' }, // Green for code actions
    tableOperations: { color: '#d32f2f' }, // Red for table operations
    workflowOperations: { color: '#f57c00' }, // Orange for workflow operations
  };

  switch (category) {
    case 'triggers':
      return <Bolt fontSize="medium" sx={iconStyles.triggers} />;
    case 'aiActions':
      return (
        <svg width="20" height="20" style={iconStyles.aiActions}>
          <use xlinkHref="#ai-stars-icon" fill="currentColor" />
        </svg>
      );
    case 'humanActions':
      return <Person fontSize="medium" sx={iconStyles.humanActions} />;
    case 'codeActions':
      return <Code fontSize="medium" sx={iconStyles.codeActions} />;
    case 'tableOperations':
      return <Storage fontSize="medium" sx={iconStyles.tableOperations} />;
    case 'workflowOperations':
      return <CallSplit fontSize="medium" sx={iconStyles.workflowOperations} />;
    default:
      return null;
  }
};
